import React, { useState, useRef, useEffect } from 'react';
import {
    Badge,
    Box,
    Popover,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Tooltip,
    IconButton,
    Chip,
    Button,
} from '@material-ui/core';
import { makeStyles, createStyles, Theme, useTheme } from '@material-ui/core/styles';
// import BellIcon from '../../icons/Bell';
import useAuth from '../../hooks/useAuth';
import { NotificationResponse, NotificationApiResponse, UnifiedNotification } from '../../types/notification';
import { chatApi } from 'src/api/chatApi';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'src/hooks/useRedux';
import { reopenSession } from 'src/store/slices/chatWidgetSlice';
import { Notifications } from '@material-ui/icons';
// ... existing code ...


const useStyles = makeStyles<Theme>((theme) =>
    createStyles({
        badge: {
            cursor: 'pointer',
        },
        popover: {
            '& .MuiPaper-root': {
                width: '400px',
                maxHeight: '500px',
                overflow: 'hidden',
            },
        },
        header: {
            padding: '16px 20px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        title: {
            fontSize: '18px',
            fontWeight: 600,
            color: '#333',
        },
        list: {
            padding: 0,
            maxHeight: '400px',
            overflowY: 'auto',
        },
        listItem: {
            padding: '12px 20px',
            borderBottom: '1px solid #f5f5f5',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: '#f9f9f9',
            },
        },
        unreadItem: {
            backgroundColor: '#f0f8ff',
            '&:hover': {
                backgroundColor: '#e6f3ff',
            },
        },
        messageContent: {
            fontSize: '14px',
            color: '#333',
            marginBottom: '4px',
        },
        messageTime: {
            fontSize: '12px',
            color: '#666',
        },
        caseKey: {
            fontSize: '12px',
            fontWeight: 500,
            color: theme.palette.primary.main,
        },
        supportUsers: {
            fontSize: '11px',
            color: '#888',
            marginTop: '2px',
        },
        emptyState: {
            padding: '40px 20px',
            textAlign: 'center',
            color: '#666',
        },
        connectionStatus: {
            fontSize: '12px',
            padding: '8px 20px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        },
        connectedIndicator: {
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#4caf50',
        },
        disconnectedIndicator: {
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#f44336',
        },
        headerActions: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        },
        markAllButton: {
            fontSize: '11px',
            padding: '4px 8px',
            minWidth: 'unset',
        },
    })
);

interface NotificationsWidgetProps {
    disabled?: boolean;
}

const NotificationsWidget: React.FC<NotificationsWidgetProps> = ({ disabled = false }) => {
    const classes = useStyles();
    const { user } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const anchorRef = useRef<HTMLButtonElement | null>(null);

    const [open, setOpen] = useState<boolean>(false);
    const [notifications, setNotifications] = useState<UnifiedNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const wsRef = useRef<WebSocket | null>(null);
    const seenNotificationIdsRef = useRef<Set<string>>(new Set());

    const getNotificationKey = (notification: { notificationId?: string; messageId?: string }) =>
        notification.notificationId || notification.messageId;

    // Transform API response to unified format
    const transformApiNotification = (apiNotification: NotificationApiResponse): UnifiedNotification => {
        const isSeenByUser = apiNotification.seenBy?.includes(user.id) || false;

        return {
            sessionId: apiNotification.sessionId,
            caseKey: apiNotification.caseKey,
            messageId: apiNotification.messageId,
            isSupportUserAssigned: apiNotification.isSupportUserAssigned,
            content: apiNotification.content,
            createdAt: apiNotification.createdAt,
            isSeen: isSeenByUser,
            supportUsers: apiNotification.supportUsers,
            seenBy: apiNotification.seenBy,
        };
    };

    // Transform socket response to unified format
    const transformSocketNotification = (socketNotification: NotificationResponse): UnifiedNotification => ({
        notificationId: socketNotification.notificationId,
        sessionId: socketNotification.sessionId,
        caseKey: socketNotification.caseKey,
        messageId: socketNotification.messageId,
        isSupportUserAssigned: socketNotification.isSupportUserAssigned,
        content: socketNotification.content,
        createdAt: socketNotification.createdAt,
        isSeen: socketNotification.isSeen,
        sessionDetails: socketNotification.sessionDetails,
    });

    const getNotifications = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await chatApi.getNotifications(user.id);

            if (response.data && Array.isArray(response.data)) {
                const transformedNotifications = response.data.map(transformApiNotification);
                transformedNotifications.forEach((notification) => {
                    const key = getNotificationKey(notification);
                    if (key) {
                        seenNotificationIdsRef.current.add(key);
                    }
                });
                setNotifications(transformedNotifications);

                const unreadNotifications = transformedNotifications.filter(n => !n.isSeen);
                setUnreadCount(unreadNotifications.length);
            }
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getNotifications();
    }, []);



    // WebSocket connection
    useEffect(() => {
        if (!user?.id || disabled) {
            return undefined;
        }

        let cancelled = false;
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
        const chatBaseUrl = process.env.REACT_APP_CHAT || '';
        const wsUrl = `${chatBaseUrl.replace(/^http/, 'ws')}/notifications/${user.id}`;

        const connectWebSocket = () => {
            if (cancelled) {
                return;
            }

            const existing = wsRef.current;
            if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
                return;
            }

            try {
                const ws = new WebSocket(wsUrl);
                wsRef.current = ws;

                ws.onopen = () => {
                    if (cancelled) {
                        ws.close();
                        return;
                    }
                    setIsConnected(true);
                };

                ws.onmessage = (event) => {
                    try {
                        const data: NotificationResponse = JSON.parse(event.data);
                        if (data.type !== 'notification') {
                            return;
                        }

                        const transformedNotification = transformSocketNotification(data);
                        const key = getNotificationKey(transformedNotification);
                        if (key && seenNotificationIdsRef.current.has(key)) {
                            return;
                        }
                        if (key) {
                            seenNotificationIdsRef.current.add(key);
                        }

                        setNotifications(prev => [transformedNotification, ...prev]);
                        if (!data.isSeen) {
                            setUnreadCount(prev => prev + 1);
                        }

                        const audio = new Audio('/sound/pop.mp3');
                        audio.play().catch(() => { });
                    } catch (e) {
                        console.error('Error parsing notification data:', e);
                    }
                };

                ws.onclose = () => {
                    if (wsRef.current === ws) {
                        wsRef.current = null;
                    }
                    setIsConnected(false);
                    if (!cancelled && !reconnectTimer) {
                        reconnectTimer = setTimeout(() => {
                            reconnectTimer = null;
                            connectWebSocket();
                        }, 3000);
                    }
                };

                ws.onerror = () => {
                    setIsConnected(false);
                };
            } catch (e) {
                console.error('Error creating WebSocket connection:', e);
                setIsConnected(false);
                if (!cancelled && !reconnectTimer) {
                    reconnectTimer = setTimeout(() => {
                        reconnectTimer = null;
                        connectWebSocket();
                    }, 3000);
                }
            }
        };

        connectWebSocket();

        return () => {
            cancelled = true;
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            const ws = wsRef.current;
            if (ws) {
                ws.onopen = null;
                ws.onmessage = null;
                ws.onerror = null;
                ws.onclose = null;
                ws.close();
                wsRef.current = null;
            }
        };
    }, [user?.id, disabled]);

    const handleBadgeClick = () => {
        setOpen(true);
        // Recalculate unread count when opening (don't reset to 0)
        const currentUnread = notifications.filter(n => !n.isSeen).length;
        setUnreadCount(currentUnread);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleNotificationClick = async (notification: UnifiedNotification) => {
        try {
            setNotifications(prev =>
                prev.map(n =>
                    (n.notificationId === notification.notificationId || n.messageId === notification.messageId)
                        ? { ...n, isSeen: true }
                        : n
                )
            );
            if (!notification.isSeen) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }

            setOpen(false);
            const response = await chatApi.getLastOpenSession(user.id);
            const lastOpenSession = response.data;

            if (lastOpenSession && lastOpenSession.caseKey === notification.caseKey) {
                console.log('in repen',);
                dispatch(reopenSession({
                    sessionId: lastOpenSession.id,
                    sessionData: lastOpenSession
                }));

            } else {
                // Case keys don't match or no last open session - navigate to chat history
                navigate('/chats-history');
            }
        } catch (e) {
            console.error('Error handling notification click:', e);
            // On error, fallback to navigating to chat history
            navigate('/chats-history');
        }
    };

    const handleMarkAllAsRead = () => {
        // Mark all notifications as read locally
        setNotifications(prev =>
            prev.map(n => ({ ...n, isSeen: true }))
        );
        chatApi.markAllAsRead(user.id);
        // Reset unread count to 0
        setUnreadCount(0);
    };




    const formatTime = (timeString: string) => {
        try {
            const date = new Date(timeString);
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            return timeString;
        }
    };

    if (disabled) {
        return null;
    }

    return (
        <>
            <Tooltip title="Chat Notifications">
                <IconButton
                    ref={anchorRef}
                    onClick={handleBadgeClick}
                    className={classes.badge}
                    size="small"
                    sx={{ p: 0 }}
                >
                    <Badge
                        sx={{
                            '& .MuiBadge-badge': {
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                            }
                        }}
                        badgeContent={unreadCount}
                        max={99}
                    >
                        <Notifications fontSize="medium" sx={{ color: '#718096' }} />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                anchorEl={anchorRef.current}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                className={classes.popover}
            >
                <Box className={classes.header}>
                    <Typography className={classes.title}>
                        Chat Notifications
                    </Typography>
                    <Box className={classes.headerActions}>
                        {unreadCount > 0 && (
                            <Button
                                size="small"
                                variant="text"
                                color="primary"
                                onClick={handleMarkAllAsRead}
                                className={classes.markAllButton}
                            >
                                Mark all read
                            </Button>
                        )}
                        <Chip
                            size="small"
                            label={`${unreadCount} new`}
                            color={unreadCount > 0 ? 'secondary' : 'default'}
                        />
                    </Box>
                </Box>

                <Box className={classes.connectionStatus}>
                    <div
                        className={
                            isConnected ? classes.connectedIndicator : classes.disconnectedIndicator
                        }
                    />
                    <Typography variant="caption">
                        {isConnected ? 'Connected' : 'Disconnected'}
                    </Typography>
                </Box>

                <List className={classes.list}>
                    {loading ? (
                        <Box className={classes.emptyState}>
                            <Typography>Loading notifications...</Typography>
                        </Box>
                    ) : error ? (
                        <Box className={classes.emptyState}>
                            <Typography color="error">{error}</Typography>
                        </Box>
                    ) : notifications.length === 0 ? (
                        <Box className={classes.emptyState}>
                            <Typography>No notifications yet</Typography>
                        </Box>
                    ) : (
                        notifications.map((notification, index) => (
                            <ListItem
                                key={notification.notificationId || notification.messageId || index}
                                className={`${classes.listItem} ${!notification.isSeen ? classes.unreadItem : ''
                                    }`}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <ListItemAvatar>
                                    <Avatar style={{ backgroundColor: '#1976d2' }}>
                                        {notification.caseKey.charAt(0)}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Box>
                                            <Typography className={classes.caseKey}>
                                                {notification.caseKey}
                                            </Typography>
                                            <Typography className={classes.messageContent}>
                                                {notification.content}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Box>
                                            <Typography className={classes.messageTime}>
                                                {formatTime(notification.createdAt)}
                                            </Typography>
                                            {notification.sessionDetails?.supportUsers?.length > 0 && (
                                                <Typography className={classes.supportUsers}>
                                                    Support: {notification.sessionDetails.supportUsers.map(u => u.name).join(', ')}
                                                </Typography>
                                            )}
                                            {!notification.sessionDetails && notification.supportUsers?.length > 0 && (
                                                <Typography className={classes.supportUsers}>
                                                    Support Users: {notification.supportUsers.length} assigned
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                />
                            </ListItem>
                        ))
                    )}
                </List>
            </Popover>
        </>
    );
};

export default NotificationsWidget;
